import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Default option values used as fallback when backend is unavailable
const defaultOptions = {
  articleStatus: [
    { value: '1', label: 'Published', variant: 'success' },
    { value: '2', label: 'Deleted', variant: 'danger' },
    { value: '3', label: 'Draft', variant: 'warning' },
    { value: '4', label: 'Scheduled', variant: 'info' }
  ],
  articleLabels: [
    { value: '1', label: 'Popular', variant: 'primary' },
    { value: '2', label: 'Featured', variant: 'success' },
    { value: '3', label: 'Trending', variant: 'info' }
  ],
  contactStatus: [
    { value: 'pending', label: 'Pending', variant: 'warning' },
    { value: 'in_progress', label: 'In Progress', variant: 'info' },
    { value: 'resolved', label: 'Resolved', variant: 'success' },
    { value: 'spam', label: 'Spam', variant: 'danger' }
  ],
  contactPriority: [
    { value: 'low', label: 'Low', variant: 'info' },
    { value: 'normal', label: 'Normal', variant: 'primary' },
    { value: 'high', label: 'High', variant: 'warning' },
    { value: 'urgent', label: 'Urgent', variant: 'danger' }
  ],
  courseLevel: [
    { value: 'beginner', label: 'Beginner', variant: 'success' },
    { value: 'intermediate', label: 'Intermediate', variant: 'warning' },
    { value: 'advanced', label: 'Advanced', variant: 'danger' }
  ],
  courseStatus: [
    { value: 'draft', label: 'Draft', variant: 'warning' },
    { value: 'published', label: 'Published', variant: 'success' },
    { value: 'archived', label: 'Archived', variant: 'secondary' }
  ],
  advertisementTypes: [],
  advertisementSlots: []
};

// Async thunks
export const fetchNavigation = createAsyncThunk(
  'config/fetchNavigation',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/config/navigation');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch navigation');
    }
  }
);

export const fetchOptions = createAsyncThunk(
  'config/fetchOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/config/options');
      const backendData = (response.data && response.data.data) || {};
      // Merge backend data with defaults so missing types always have values
      return { ...defaultOptions, ...backendData };
    } catch (error) {
      console.log('Using default options data (backend not ready)');
      return defaultOptions;
    }
  }
);

export const fetchTheme = createAsyncThunk(
  'config/fetchTheme',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/settings');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch theme');
    }
  }
);

export const fetchRolesAndPermissions = createAsyncThunk(
  'config/fetchRolesAndPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const [rolesRes, permissionsRes] = await Promise.all([
        api.get('/auth/roles'),
        api.get('/auth/permissions')
      ]);
      return {
        roles: rolesRes.data.data || rolesRes.data,
        permissions: permissionsRes.data.data || permissionsRes.data
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch roles/permissions');
    }
  }
);

const initialState = {
  navigation: [],
  options: {},
  theme: {},
  roles: [],
  permissions: [],
  loading: {
    navigation: false,
    options: false,
    theme: false,
    rolesPermissions: false
  },
  error: {
    navigation: null,
    options: null,
    theme: null,
    rolesPermissions: null
  },
  initialized: false
};

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    clearConfigError: (state, action) => {
      if (action.payload) {
        state.error[action.payload] = null;
      } else {
        state.error = { navigation: null, options: null, theme: null, rolesPermissions: null };
      }
    },
    setInitialized: (state) => {
      state.initialized = true;
    }
  },
  extraReducers: (builder) => {
    // Fetch Navigation
    builder
      .addCase(fetchNavigation.pending, (state) => {
        state.loading.navigation = true;
        state.error.navigation = null;
      })
      .addCase(fetchNavigation.fulfilled, (state, action) => {
        state.loading.navigation = false;
        state.navigation = action.payload.menu || action.payload;
      })
      .addCase(fetchNavigation.rejected, (state, action) => {
        state.loading.navigation = false;
        state.error.navigation = action.payload;
      });

    // Fetch Options
    builder
      .addCase(fetchOptions.pending, (state) => {
        state.loading.options = true;
        state.error.options = null;
      })
      .addCase(fetchOptions.fulfilled, (state, action) => {
        state.loading.options = false;
        state.options = action.payload;
      })
      .addCase(fetchOptions.rejected, (state, action) => {
        state.loading.options = false;
        state.error.options = action.payload;
      });

    // Fetch Theme
    builder
      .addCase(fetchTheme.pending, (state) => {
        state.loading.theme = true;
        state.error.theme = null;
      })
      .addCase(fetchTheme.fulfilled, (state, action) => {
        state.loading.theme = false;
        state.theme = action.payload;
      })
      .addCase(fetchTheme.rejected, (state, action) => {
        state.loading.theme = false;
        state.error.theme = action.payload;
      });

    // Fetch Roles & Permissions
    builder
      .addCase(fetchRolesAndPermissions.pending, (state) => {
        state.loading.rolesPermissions = true;
        state.error.rolesPermissions = null;
      })
      .addCase(fetchRolesAndPermissions.fulfilled, (state, action) => {
        state.loading.rolesPermissions = false;
        state.roles = action.payload.roles;
        state.permissions = action.payload.permissions;
      })
      .addCase(fetchRolesAndPermissions.rejected, (state, action) => {
        state.loading.rolesPermissions = false;
        state.error.rolesPermissions = action.payload;
      });
  }
});

export const { clearConfigError, setInitialized } = configSlice.actions;

// Selectors
export const selectNavigation = (state) => state.config.navigation;
export const selectOptions = (state) => state.config.options;
export const selectTheme = (state) => state.config.theme;
export const selectRoles = (state) => state.config.roles;
export const selectPermissions = (state) => state.config.permissions;
export const selectConfigLoading = (state) => state.config.loading;
export const selectConfigError = (state) => state.config.error;
export const selectConfigInitialized = (state) => state.config.initialized;

// Helper selectors for specific options
export const selectArticleStatusOptions = (state) => state.config.options.articleStatus || [];
export const selectArticleLabelOptions = (state) => state.config.options.articleLabels || [];
export const selectContactStatusOptions = (state) => state.config.options.contactStatus || [];
export const selectContactPriorityOptions = (state) => state.config.options.contactPriority || [];
export const selectCourseLevelOptions = (state) => state.config.options.courseLevel || [];
export const selectCourseStatusOptions = (state) => state.config.options.courseStatus || [];
export const selectAdvertisementTypeOptions = (state) => state.config.options.advertisementTypes || [];
export const selectAdvertisementSlotOptions = (state) => state.config.options.advertisementSlots || [];

export default configSlice.reducer;
